import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { Store } from '@ngxs/store';

import { StudentDocumentVaultService } from '../../../services/student-document-vault/student-document-vault.service';
import { MyInstituteService } from '../../../services/institute/myinstitute/my-institute.service';
import { EssayPilotService } from '../../../services/essay-pilot/essay-pilot.service';
import {
  StudentDocumentVaultResponse,
  DocumentCategoryEnum,
  DocumentTypeEnum,
  DocumentStatusEnum,
  EssayTypeEnum,
  CommonEssaySubTypeEnum,
  LorSubTypeEnum
} from '../../../models/student-document-vault/student-document-vault';
import { CourseLevelEnum } from '../../../models/enums/courseLevelEnum';
import { LottieAnimationComponent } from '../../../shared/components/lottie-animation/lottie-animation.component';

@Component({
  selector: 'app-document-upload-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TooltipModule,
    LottieAnimationComponent
  ],
  templateUrl: './document-upload-modal.component.html',
  styleUrls: ['./document-upload-modal.component.scss']
})
export class DocumentUploadModalComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() editingDoc: StudentDocumentVaultResponse | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSaved = new EventEmitter<void>();

  uploadForm!: FormGroup;
  selectedFile: File | null = null;
  isSubmitting: boolean = false;
  isEditing: boolean = false;
  editingDocId: string | null = null;
  existingFileName: string | null = null;
  isSuccessState: boolean = false;
  successDocumentName: string = '';
  selectedDocType: DocumentTypeEnum = DocumentTypeEnum.Drive;
  isMissingRequest: boolean = false;
  remarks: string | null = null;

  categories = [
    { label: 'Essay Pilot', value: DocumentCategoryEnum.EssayPilot },
    { label: 'Letter of Recommendation', value: DocumentCategoryEnum.LOR },
    { label: 'Academics', value: DocumentCategoryEnum.Academics },
    { label: 'Test Scores', value: DocumentCategoryEnum.TestScores },
    { label: 'Identity Documents', value: DocumentCategoryEnum.Identity },
    { label: 'Other Documents', value: DocumentCategoryEnum.Other }
  ];

  essayTypes = [
    { label: 'Common Essay', value: EssayTypeEnum.CommonEssay },
    { label: 'Institute', value: EssayTypeEnum.Institute }
  ];

  get filteredCommonEssaySubTypes() {
    const degreePref = this.store.selectSnapshot(state => state.user?.degreePreference);
    
    // Bachelors (Value 1)
    if (degreePref === 0) {
      return [
        { label: 'Common App Essay', value: CommonEssaySubTypeEnum.CommonAppEssay },
        { label: 'UCAS Essay', value: CommonEssaySubTypeEnum.UCASEssay },
        { label: 'SOP', value: CommonEssaySubTypeEnum.SOP },
        { label: 'Brag Sheet', value: CommonEssaySubTypeEnum.BragSheet },
        { label: 'Scholarship Essay', value: CommonEssaySubTypeEnum.ScholarshipEssay },
        { label: 'Others', value: CommonEssaySubTypeEnum.Others }
      ];
    } 
    
    // Masters (Value 8) or Default (Undefined/Null)
    if (degreePref === 7) {
      return [
        { label: 'SOP', value: CommonEssaySubTypeEnum.SOP },
        { label: 'Brag Sheet', value: CommonEssaySubTypeEnum.BragSheet },
        { label: 'Scholarship Essay', value: CommonEssaySubTypeEnum.ScholarshipEssay },
        { label: 'Others', value: CommonEssaySubTypeEnum.Others }
      ];
    }
    
    // Any other degree preference returns empty
    return [];
  }

  lorSubTypes = [
    { label: 'Professional', value: LorSubTypeEnum.Professional },
    { label: 'Academic', value: LorSubTypeEnum.Academic },
    { label: 'Other', value: LorSubTypeEnum.Other }
  ];

  myInstitutes: any[] = [];
  isLoadingInstitutes = false;

  essayQuestions: { label: string, value: number, text: string }[] = [];
  isLoadingQuestions: boolean = false;
  hasFetchedQuestions: boolean = false;
  currentQuestionId: number | null = null;

  commonEssays: any[] = [];
  isLoadingCommonEssays: boolean = false;

  DocumentTypeEnum = DocumentTypeEnum;
  DocumentCategoryEnum = DocumentCategoryEnum;
  EssayTypeEnum = EssayTypeEnum;
  CommonEssaySubTypeEnum = CommonEssaySubTypeEnum;
  LorSubTypeEnum = LorSubTypeEnum;

  get isEssayCategory(): boolean {
    return this.uploadForm.get('Category')?.value === DocumentCategoryEnum.EssayPilot;
  }

  constructor(
    private docsService: StudentDocumentVaultService,
    private myInstituteService: MyInstituteService,
    private essayPilotService: EssayPilotService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchMyInstitutes();
  }

  initForm(): void {
    this.uploadForm = this.fb.group({
      Title: ['', Validators.required],
      Category: [DocumentCategoryEnum.EssayPilot, Validators.required],
      DriveUrl: [''],
      EssayType: [null],
      InstituteId: [null],
      EssayQuestionId: [null],
      QuestionNumber: [null],
      CommonEssayId: [null],
      CommonEssaySubType: [null],
      LorSubType: [null]
    });

    this.uploadForm.get('Category')?.valueChanges.subscribe(val => {
      this.onCategoryChange(val);
    });

    this.uploadForm.get('EssayType')?.valueChanges.subscribe(val => {
      this.onEssayTypeChange(val);
    });

    this.uploadForm.get('CommonEssaySubType')?.valueChanges.subscribe(val => {
      this.onCommonEssaySubTypeChange(val);
    });
  }

  onCategoryChange(category: DocumentCategoryEnum): void {
    if (category !== DocumentCategoryEnum.EssayPilot) {
      this.uploadForm.patchValue({ EssayType: null, InstituteId: null, EssayQuestionId: null, QuestionNumber: null, CommonEssayId: null, CommonEssaySubType: null });
      this.uploadForm.get('EssayType')?.clearValidators();
    } else {
      this.uploadForm.get('EssayType')?.setValidators([Validators.required]);
    }

    if (category !== DocumentCategoryEnum.LOR) {
      this.uploadForm.patchValue({ LorSubType: null });
      this.uploadForm.get('LorSubType')?.clearValidators();
    } else {
      this.uploadForm.get('LorSubType')?.setValidators([Validators.required]);
    }

    this.uploadForm.get('EssayType')?.updateValueAndValidity();
    this.uploadForm.get('LorSubType')?.updateValueAndValidity();
    this.updateCascadeValidators();
  }

  onEssayTypeChange(type: EssayTypeEnum | null): void {
    this.uploadForm.patchValue({ InstituteId: null, EssayQuestionId: null, QuestionNumber: null, CommonEssayId: null, CommonEssaySubType: null });
    this.essayQuestions = [];
    this.commonEssays = [];
    this.updateCascadeValidators();
  }

  onCommonEssaySubTypeChange(subType: CommonEssaySubTypeEnum | null): void {
    this.uploadForm.patchValue({ CommonEssayId: null });
    this.commonEssays = [];
    if (subType !== null && subType !== undefined) {
      this.fetchCommonEssays(subType);
    }
  }

  updateCascadeValidators(): void {
    const category = this.uploadForm.get('Category')?.value;
    const type = this.uploadForm.get('EssayType')?.value;

    this.uploadForm.get('InstituteId')?.clearValidators();
    this.uploadForm.get('EssayQuestionId')?.clearValidators();
    this.uploadForm.get('CommonEssayId')?.clearValidators();
    this.uploadForm.get('CommonEssaySubType')?.clearValidators();

    if (category === DocumentCategoryEnum.EssayPilot) {
      if (type === EssayTypeEnum.Institute) {
        this.uploadForm.get('InstituteId')?.setValidators([Validators.required]);
        this.uploadForm.get('EssayQuestionId')?.setValidators([Validators.required]);
      } else if (type === EssayTypeEnum.CommonEssay) {
        this.uploadForm.get('CommonEssayId')?.setValidators([Validators.required]);
        this.uploadForm.get('CommonEssaySubType')?.setValidators([Validators.required]);
      }
    }

    this.uploadForm.get('InstituteId')?.updateValueAndValidity();
    this.uploadForm.get('EssayQuestionId')?.updateValueAndValidity();
    this.uploadForm.get('CommonEssayId')?.updateValueAndValidity();
    this.uploadForm.get('CommonEssaySubType')?.updateValueAndValidity();
  }

  fetchMyInstitutes(): void {
    if (this.myInstitutes.length > 0 || this.isLoadingInstitutes) return;
    this.isLoadingInstitutes = true;
    this.myInstituteService.getMyInstitutes().subscribe({
      next: (res: any) => {
        const dataArr = Array.isArray(res) ? res : (res && res.result && Array.isArray(res.result) ? res.result : null);
        if (dataArr) {
          this.myInstitutes = dataArr.map((inst: any) => {
            const iName = inst.instituteName || inst.InstituteName || '';
            const cName = inst.courseName || inst.CourseName || '';
            return {
              id: inst.id ?? inst.Id,
              instituteName: iName,
              courseLevel: inst.courseLevel ?? inst.CourseLevel,
              courseName: cName,
              displayName: iName ? (cName ? `${iName} - ${cName}` : iName) : (cName || 'Selected Institute')
            };
          });
        }
        this.isLoadingInstitutes = false;
      },
      error: () => this.isLoadingInstitutes = false
    });
  }

  fetchCommonEssays(subType?: CommonEssaySubTypeEnum | null): void {
    if (subType === null || subType === undefined) return;
    this.isLoadingCommonEssays = true;

    const degreePref = this.store.selectSnapshot(state => state.user?.degreePreference);
    const courseLevel = degreePref !== undefined && degreePref !== null ? degreePref : 0;
    const category = subType - 1; // Map CommonEssaySubTypeEnum to CommonEssayQuestionCategoryEnum (0-indexed)

    this.essayPilotService.getCommonEssayQuestions(courseLevel, category).subscribe({
      next: (res: any) => {
        const dataArr = Array.isArray(res) ? res : 
                        (res && res.result && Array.isArray(res.result) ? res.result : 
                        (res && res.result?.data && Array.isArray(res.result.data) ? res.result.data :
                        (res && res.data && Array.isArray(res.data) ? res.data : null)));
        if (dataArr) {
          this.commonEssays = dataArr.map((essay: any) => ({
            id: essay.id || essay.Id,
            questionText: essay.question || essay.Question || essay.questionText || essay.QuestionText || 'Common Essay'
          }));
        }
        this.isLoadingCommonEssays = false;
      },
      error: () => this.isLoadingCommonEssays = false
    });
  }

  onInstituteSelect(instituteId: string): void {
    this.uploadForm.patchValue({ EssayQuestionId: null, QuestionNumber: null });
    this.essayQuestions = [];
    this.hasFetchedQuestions = false;
    if (!instituteId) return;

    const selectedInst = this.myInstitutes.find(x => x.id === instituteId);
    if (!selectedInst) return;

    this.isLoadingQuestions = true;

    const requestParams: any = {
      instituteId: selectedInst.id || '',
      courseLevel: selectedInst.courseLevel ?? 1
    };

    if (selectedInst.courseLevel !== CourseLevelEnum.BachelorsDegree) {
      requestParams.courseName = selectedInst.courseName || '';
    }

    this.essayPilotService.getInstituteQuestions(requestParams).subscribe({
      next: (res: any) => {
        this.hasFetchedQuestions = true;
        const qData = res?.result ? res.result : res;
        if (qData) {
          const questions = [];
          
          const q1 = qData.question1 || qData.Question1;
          const q2 = qData.question2 || qData.Question2;
          const q3 = qData.question3 || qData.Question3;
          const q4 = qData.question4 || qData.Question4;
          const q5 = qData.question5 || qData.Question5;

          if (q1) questions.push({ label: 'Question 1', value: 1, text: q1 });
          if (q2) questions.push({ label: 'Question 2', value: 2, text: q2 });
          if (q3) questions.push({ label: 'Question 3', value: 3, text: q3 });
          if (q4) questions.push({ label: 'Question 4', value: 4, text: q4 });
          if (q5) questions.push({ label: 'Question 5', value: 5, text: q5 });
          
          this.essayQuestions = questions;
          this.currentQuestionId = qData.id || qData.Id; 
        }
        this.isLoadingQuestions = false;
        this.updateCascadeValidators();
      },
      error: () => {
        this.isLoadingQuestions = false;
        this.hasFetchedQuestions = true;
        this.updateCascadeValidators();
      }
    });
  }

  onQuestionSelect(questionNumber: number): void {
     this.uploadForm.patchValue({ QuestionNumber: questionNumber, CustomQuestionText: null });
  }

  setDocType(type: DocumentTypeEnum): void {
    this.uploadForm.get('DriveUrl')?.clearValidators();
    if (type === DocumentTypeEnum.Drive) {
       this.selectedFile = null;
       this.uploadForm.get('DriveUrl')?.setValidators([
           Validators.required, 
           Validators.pattern('^https?://.+')
       ]);
    } else {
       this.uploadForm.get('DriveUrl')?.setValue('', { emitEvent: false });
    }
    this.uploadForm.get('DriveUrl')?.updateValueAndValidity();
    this.selectedDocType = type;
  }

  getCommonEssayText(id: any): string {
    const essay = this.commonEssays.find(e => e.id === id);
    return essay ? `${essay.questionText}` : '';
  }

  getInstituteQuestionText(val: any): string {
    const q = this.essayQuestions.find(e => e.value === val);
    return q ? q.text : '';
  }

  showForCreate(category?: DocumentCategoryEnum, docType?: DocumentTypeEnum): void {
    this.uploadForm.reset({
      Category: category || DocumentCategoryEnum.EssayPilot
    });
    this.setDocType(docType || DocumentTypeEnum.Direct);
    this.currentQuestionId = null;
    this.selectedFile = null;
    this.isEditing = false;
    this.editingDocId = null;
    this.existingFileName = null;
    this.isMissingRequest = false;
    this.remarks = null;
    this.visible = true;
    this.visibleChange.emit(true);
  }

  showForEdit(doc: StudentDocumentVaultResponse): void {
    this.existingFileName = null;
    this.selectedFile = null;
    this.docsService.getDocumentVaultById(doc.id).subscribe({
      next: (res: any) => {
        const freshDoc = res.responseData || res.result || res;
        this.isEditing = true;
        this.editingDocId = freshDoc.id || doc.id;
        this.uploadForm.reset();
        
        // Ensure institutes are fetched
        this.fetchMyInstitutes();
        
        const instId = freshDoc.instituteId || doc.instituteId;
        this.uploadForm.patchValue({
          Title: freshDoc.title || doc.title,
          Category: freshDoc.category || doc.category,
          InstituteId: instId
        });

        this.isMissingRequest = (freshDoc.status || doc.status) === DocumentStatusEnum.Missing;
        this.remarks = freshDoc.remarks || doc.remarks;
        const instituteId = freshDoc.instituteId || doc.instituteId;
        
        if (this.isMissingRequest) {
          // If it's a missing request, student can't change category or title
          this.uploadForm.get('Category')?.disable();
          this.uploadForm.get('Title')?.disable();
        } else {
          this.uploadForm.get('Category')?.enable();
          this.uploadForm.get('Title')?.enable();
        }
        this.uploadForm.get('InstituteId')?.enable();
        
        if (instituteId) {
          this.onInstituteSelect(instituteId);
          // Safety re-patch to handle potential timing issues with PrimeNG dropdown
          setTimeout(() => {
            this.uploadForm.get('InstituteId')?.patchValue(instituteId, { emitEvent: false });
          }, 200);
        }
        
        this.setDocType(freshDoc.docType || doc.docType);
        
        const activePath = freshDoc.path || doc.path;

        if (freshDoc.docType === DocumentTypeEnum.Drive && activePath) {
            this.uploadForm.patchValue({ DriveUrl: activePath });
        } else if (freshDoc.docType === DocumentTypeEnum.Direct && activePath) {
            try {
                let extractedName = activePath.split('/').pop() || '';
                const queryIdx = extractedName.indexOf('?');
                if (queryIdx !== -1) extractedName = extractedName.substring(0, queryIdx);
                this.existingFileName = decodeURIComponent(extractedName);
                
                if (this.existingFileName.includes('_')) {
                   const parts = this.existingFileName.split('_');
                   if (parts[0].length >= 32) {
                       this.existingFileName = parts.slice(1).join('_');
                   }
                }
            } catch {
                this.existingFileName = "Current Attached File";
            }
        }

        const eInfo = freshDoc.essayInfo || doc.essayInfo;

        if (freshDoc.category === DocumentCategoryEnum.EssayPilot && eInfo) {
           this.uploadForm.patchValue({ EssayType: eInfo.essayType });
           
           if (eInfo.essayType === EssayTypeEnum.Institute) {
              if (eInfo.questionNumber || eInfo.essayQuestionId) {
                  const qNum = eInfo.questionNumber || eInfo.essayQuestionId;
                  const waitForQuestions = () => {
                      if (this.isLoadingQuestions) {
                          setTimeout(waitForQuestions, 50);
                      } else {
                          this.uploadForm.patchValue({ EssayQuestionId: qNum });
                      }
                  };
                  waitForQuestions();
              }
           } else if (eInfo.essayType === EssayTypeEnum.CommonEssay) {
                if (eInfo.commonEssaySubType) {
                   this.uploadForm.patchValue({ CommonEssaySubType: eInfo.commonEssaySubType });
                }
                if (eInfo.commanEssayQuestionId) {
                   const attemptCommon = () => {
                      if (this.isLoadingCommonEssays) {
                          setTimeout(attemptCommon, 50);
                      } else {
                          this.uploadForm.patchValue({ CommonEssayId: eInfo.commanEssayQuestionId });
                      }
                   };
                   attemptCommon();
                }
            }
         }

         if (freshDoc.category === DocumentCategoryEnum.LOR && freshDoc.lorInfo) {
            this.uploadForm.patchValue({ LorSubType: freshDoc.lorInfo.lorSubType });
         }
        
        this.visible = true;
        this.visibleChange.emit(true);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not fetch latest document details.' });
      }
    });
  }

  isDragging = false;

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isSubmitting) {
      this.isDragging = true;
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    if (this.isSubmitting) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    if (event.target.files && event.target.files.length > 0) {
      this.handleFile(event.target.files[0]);
      // Reset input value to allow selecting same file again if needed
      event.target.value = '';
    }
  }

  handleFile(file: File) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    
    if (!allowed.includes(ext || '')) {
      this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Please upload only PDF, Word, or Image documents' });
      this.selectedFile = null;
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({ severity: 'error', summary: 'File Too Large', detail: 'Maximum allowed file size is 5MB' });
      this.selectedFile = null;
      return;
    }
    
    this.selectedFile = file;
    this.setDocType(DocumentTypeEnum.Direct);
  }

  hideUploadDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.uploadForm.reset({
      Category: DocumentCategoryEnum.EssayPilot
    });
    this.uploadForm.get('Category')?.enable();
    this.uploadForm.get('Title')?.enable();
    this.uploadForm.get('InstituteId')?.enable();
    this.essayQuestions = [];
    this.currentQuestionId = null;
    this.selectedFile = null;
    this.isSubmitting = false;
    this.isEditing = false;
    this.editingDocId = null;
    this.existingFileName = null;
    this.isSuccessState = false;
    this.successDocumentName = '';
  }

  submitUpload(): void {
    if (this.uploadForm.invalid) {
      this.uploadForm.markAllAsTouched();
      return;
    }

    const formValues = this.uploadForm.getRawValue();
    
    if (!(this.isEditing && this.existingFileName) && this.selectedDocType === DocumentTypeEnum.Direct && !this.selectedFile) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select a file to upload' });
      return;
    }

    const formData = new FormData();
    formData.append('Title', formValues.Title);
    formData.append('Category', formValues.Category.toString());
    formData.append('DocType', this.selectedDocType.toString());

    if (this.selectedDocType === DocumentTypeEnum.Direct && this.selectedFile) {
      formData.append('Document', this.selectedFile);
      formData.append('File', this.selectedFile);
    } else if (this.selectedDocType === DocumentTypeEnum.Drive) {
      formData.append('DriveUrl', formValues.DriveUrl || '');
    }

    if (formValues.Category === DocumentCategoryEnum.EssayPilot && formValues.EssayType) {
      const essayInfo: any = {
        EssayType: formValues.EssayType
      };
      
      if (formValues.EssayType === EssayTypeEnum.Institute) {
        if (this.currentQuestionId) {
           essayInfo.EssayQuestionId = this.currentQuestionId;
        }
        if (formValues.EssayQuestionId) {
           essayInfo.QuestionNumber = formValues.EssayQuestionId;
        }
      } else if (formValues.EssayType === EssayTypeEnum.CommonEssay) {
        if (formValues.CommonEssayId) {
          essayInfo.CommanEssayQuestionId = formValues.CommonEssayId;
        }
        if (formValues.CommonEssaySubType) {
          essayInfo.CommonEssaySubType = formValues.CommonEssaySubType;
        }
      }

      formData.append('EssayInfo', JSON.stringify(essayInfo));
    }

    if (formValues.InstituteId) {
      formData.append('InstituteId', formValues.InstituteId);
    }

    if (formValues.Category === DocumentCategoryEnum.LOR && formValues.LorSubType) {
      const lorInfo = {
        lorSubType: formValues.LorSubType
      };
      formData.append('LorInfo', JSON.stringify(lorInfo));
    }

    this.isSubmitting = true;

    if (this.isEditing && this.editingDocId) {
      this.docsService.updateDocumentVault(this.editingDocId, formData).subscribe({
        next: (res: any) => {
          if (res && res.id) {
            this.successDocumentName = formValues.Title;
            this.isSuccessState = true;
            setTimeout(() => {
                this.hideUploadDialog();
                this.onSaved.emit();
            }, 2500);
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update document' });
          }
          this.isSubmitting = false;
        },
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update document' });
          this.isSubmitting = false;
        }
      });
    } else {
      this.docsService.uploadDocumentVault(formData).subscribe({
        next: (res: any) => {
          if (res && res.id) {
            this.successDocumentName = formValues.Title;
            this.isSuccessState = true;
            setTimeout(() => {
                this.hideUploadDialog();
                this.onSaved.emit();
            }, 2500);
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload document' });
          }
          this.isSubmitting = false;
        },
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload document' });
          this.isSubmitting = false;
        }
      });
    }
  }
}
